import { useCallback, useSyncExternalStore } from 'react';
import { useOptionalAuth } from '../context/AuthContext';

/** The Accounts list's search text (CONTEXT.md "Accounts list"): one text shared by
 *  the expanded rail's search, the narrow rail's search panel and the phone's
 *  Accounts sheet, so picking an Account and coming back finds it as it was left.
 *  Kept in this browser only (localStorage), per login. */
const KEY_PREFIX = 'om_account_search:';

const listeners = new Set<() => void>();
const cache = new Map<string, string>();

function read(key: string): string {
  const cached = cache.get(key);
  if (cached !== undefined) return cached;
  let value = '';
  try {
    value = localStorage.getItem(key) ?? '';
  } catch {
    /* blocked storage: start empty */
  }
  cache.set(key, value);
  return value;
}

function write(key: string, value: string) {
  cache.set(key, value);
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* blocked storage: the text just doesn't outlive the tab */
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  // Another tab of the console typed or cleared it.
  const onStorage = (e: StorageEvent) => {
    if (e.key?.startsWith(KEY_PREFIX)) {
      cache.delete(e.key);
      listener();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

export function useAccountSearch(): [string, (value: string) => void] {
  const userId = useOptionalAuth()?.user?.id ?? 'anon';
  const key = KEY_PREFIX + userId;
  const value = useSyncExternalStore(subscribe, () => read(key));
  const setValue = useCallback((next: string) => write(key, next), [key]);
  return [value, setValue];
}
